/**
 * Global Keyboard Shortcuts Hook
 *
 * Provides consistent keyboard navigation across the platform.
 * Shortcuts are disabled when focus is in text inputs or textareas.
 */

import { useCallback, useEffect } from "react";
import { useLocation } from "wouter";

export interface KeyboardShortcut {
  key: string;
  description: string;
  action: () => void;
  /** Require Cmd/Ctrl modifier */
  meta?: boolean;
  /** Require Shift modifier */
  shift?: boolean;
  /** Require Alt modifier */
  alt?: boolean;
  /** Only work on specific pages (regex pattern) */
  pages?: RegExp;
  /** Category for grouping in help modal */
  category: "navigation" | "actions" | "ui" | "editing";
}

interface UseKeyboardShortcutsOptions {
  /** Additional custom shortcuts */
  shortcuts?: KeyboardShortcut[];
  /** Disable all shortcuts */
  disabled?: boolean;
}

/**
 * Check if the current focus is in an editable element
 */
function isEditableElement(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  // Check for contenteditable
  if ((activeElement as HTMLElement).isContentEditable) {
    return true;
  }

  return false;
}

/**
 * Hook for managing global keyboard shortcuts
 */
export function useKeyboardShortcuts(
  options: UseKeyboardShortcutsOptions = {}
) {
  const { shortcuts: customShortcuts = [], disabled = false } = options;
  const [location, setLocation] = useLocation();

  // Default global shortcuts
  const defaultShortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts (single keys, no modifiers)
    {
      key: "g",
      description: "Go to Dashboard",
      action: () => setLocation("/dashboard"),
      category: "navigation",
    },
    {
      key: "t",
      description: "Go to Transactions",
      action: () => setLocation("/transactions"),
      category: "navigation",
    },
    {
      key: "d",
      description: "Go to Documents",
      action: () => setLocation("/documents"),
      category: "navigation",
    },
    {
      key: "r",
      description: "Go to Reports",
      action: () => setLocation("/statements"),
      category: "navigation",
    },
    {
      key: "v",
      description: "Go to VAT",
      action: () => setLocation("/vat"),
      category: "navigation",
    },

    // Action shortcuts with modifiers
    {
      key: "n",
      description: "New Transaction",
      action: () => setLocation("/transactions?action=new"),
      shift: true,
      category: "actions",
    },
    {
      key: "i",
      description: "Import Data",
      action: () => setLocation("/bank-import"),
      shift: true,
      category: "actions",
    },
    {
      key: "u",
      description: "Upload Documents",
      action: () => setLocation("/file-inbox"),
      shift: true,
      category: "actions",
    },

    // UI shortcuts
    {
      key: "/",
      description: "Focus Search",
      action: () => {
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[type="search"], input[placeholder*="search" i], input[placeholder*="Search" i]'
        );
        if (searchInput) {
          searchInput.focus();
        }
      },
      category: "ui",
    },
    {
      key: "Escape",
      description: "Close Modal/Dialog",
      action: () => {
        // Escape is typically handled by dialogs themselves
      },
      category: "ui",
    },
  ];

  const allShortcuts = [...defaultShortcuts, ...customShortcuts];

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return;

      // Skip if in editable element (except for specific shortcuts)
      if (isEditableElement()) {
        // Only allow Escape and meta shortcuts in editable elements
        if (event.key !== "Escape" && !event.metaKey && !event.ctrlKey) {
          return;
        }
      }

      // Skip if a modal is open (Command Palette handles its own shortcuts)
      const hasOpenModal = document.querySelector(
        '[role="dialog"][data-state="open"]'
      );
      if (hasOpenModal && event.key !== "Escape") {
        return;
      }

      for (const shortcut of allShortcuts) {
        const keyMatches =
          event.key.toLowerCase() === shortcut.key.toLowerCase();
        const metaMatches = shortcut.meta
          ? event.metaKey || event.ctrlKey
          : !(event.metaKey || event.ctrlKey);
        const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatches = shortcut.alt ? event.altKey : !event.altKey;
        const pageMatches = shortcut.pages
          ? shortcut.pages.test(location)
          : true;

        if (
          keyMatches &&
          metaMatches &&
          shiftMatches &&
          altMatches &&
          pageMatches
        ) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [allShortcuts, disabled, location]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return {
    shortcuts: allShortcuts,
  };
}

/**
 * Get formatted shortcut key display
 */
export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().includes("MAC");
  const parts: string[] = [];

  if (shortcut.meta) {
    parts.push(isMac ? "⌘" : "Ctrl");
  }
  if (shortcut.shift) {
    parts.push(isMac ? "⇧" : "Shift");
  }
  if (shortcut.alt) {
    parts.push(isMac ? "⌥" : "Alt");
  }

  // Format special keys
  let keyDisplay = shortcut.key;
  if (shortcut.key === "Escape") keyDisplay = "Esc";
  if (shortcut.key === "/") keyDisplay = "/";

  parts.push(keyDisplay.toUpperCase());

  return parts.join(isMac ? "" : "+");
}

export default useKeyboardShortcuts;
